import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { ClipboardList, AlertCircle, TrendingUp } from "lucide-react";

const PRIORITY_COLORS = {
  urgent: { bg: "#FEE2E2", text: "#DC2626" },
  high: { bg: "#FEF3C7", text: "#D97706" },
  medium: { bg: "#DBEAFE", text: "#1D4ED8" },
  low: { bg: "#F5F3EF", text: "#57534E" },
};
const STATUS_COLORS = {
  pending_review: { bg: "#FEF3C7", text: "#D97706" },
  in_progress: { bg: "#DBEAFE", text: "#1D4ED8" },
  completed: { bg: "#D1FAE5", text: "#059669" },
  cancelled: { bg: "#FEE2E2", text: "#DC2626" },
  on_hold: { bg: "#F3E8FF", text: "#7C3AED" },
};
const fmt = (s) => s?.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ") || "";

export default function Dashboard() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    api.get("/requests").then((r) => {
      const open = r.data.filter((t) => !["completed", "cancelled"].includes(t.status));
      const order = { urgent: 0, high: 1, medium: 2, low: 3 };
      open.sort((a, b) => {
        const pa = order[a.priority] ?? 4;
        const pb = order[b.priority] ?? 4;
        if (pa !== pb) return pa - pb;
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
      setTasks(open);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="agency-dashboard">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-serif text-3xl font-bold mb-1" style={{ color: "#292524" }}>Dashboard</h1>
            <p className="text-sm" style={{ color: "#57534E" }}>Welcome back, {user?.name}</p>
          </div>
          <Link
            to="/analytics"
            data-testid="view-analytics-link"
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg transition-colors hover:bg-amber-50"
            style={{ color: "#D97706", border: "1px solid #FDE68A" }}
          >
            <TrendingUp size={13} />
            Analytics
          </Link>
        </div>

        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "#E7E5E4" }}>
          <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E7E5E4" }}>
            <div className="flex items-center gap-2">
              <h2 className="font-serif text-lg font-semibold" style={{ color: "#292524" }}>Open Tasks</h2>
              {!loading && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#D97706" }}>
                  {tasks.length}
                </span>
              )}
            </div>
            <Link to="/requests" className="text-xs font-medium" style={{ color: "#D97706" }}>View board →</Link>
          </div>

          {loading ? (
            <div className="px-6 py-10 text-center text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
          ) : tasks.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <ClipboardList size={28} className="mx-auto mb-2" style={{ color: "#E7E5E4" }} />
              <p className="text-sm font-medium" style={{ color: "#292524" }}>All caught up!</p>
              <p className="text-xs mt-1" style={{ color: "#A8A29E" }}>No open tasks right now</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: "#F5F3EF" }}>
              {tasks.map((req) => {
                const sc = STATUS_COLORS[req.status] || { bg: "#F5F3EF", text: "#57534E" };
                const pc = PRIORITY_COLORS[req.priority];
                const isOverdue = req.due_date && req.due_date < today;
                return (
                  <Link
                    key={req.id}
                    to={`/requests/${req.id}`}
                    data-testid={`open-task-${req.id}`}
                    className="px-6 py-4 flex items-center gap-4 hover:bg-stone-50 transition-colors"
                    style={{ display: "flex" }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium truncate" style={{ color: "#292524" }}>{req.title}</p>
                        {isOverdue && <AlertCircle size={13} style={{ color: "#DC2626" }} />}
                      </div>
                      <p className="text-xs mt-0.5 truncate" style={{ color: "#A8A29E" }}>
                        {req.client_name}
                        {req.assigned_to_name ? ` · ${req.assigned_to_name}` : ""}
                        {req.due_date ? ` · Due ${req.due_date}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {pc && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full hidden sm:inline-block" style={{ background: pc.bg, color: pc.text }}>
                          {fmt(req.priority)}
                        </span>
                      )}
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: sc.bg, color: sc.text }}>
                        {fmt(req.status)}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
